import { useState } from 'react';
import { apiBaseUrl } from '../authConfig.js';
import { Panel, CountKpi } from '../components/CommandCentreParts.js';
import { EmptyState } from '../components/NoData.js';
import { useApi, type GetToken } from '../lib/useApi.js';
import { gbp, count, faintIfNull, statusPill, NO_DATA } from '../lib/format.js';

interface Campaign {
  id: string; name: string; status: string;
  channelType: string | null; biddingStrategy: string | null;
  dailyBudgetGbp: number | null; monthlyBudgetGbp: number | null;
  adGroups: number; keywords: number;
  hasServed: boolean;
  impressions: number | null; clicks: number | null;
  costGbp: number | null; conversions: number | null;
  costPerClickGbp: number | null;
}

interface CampaignsResponse {
  window: { from: string; to: string; days: number };
  lastSync: { finishedUtc: string | null; status: string | null; running: boolean; scheduleNote: string };
  campaigns: Campaign[];
  generatedUtc: string;
}

export function Campaigns({ getToken }: { getToken: GetToken }): JSX.Element {
  const { data, error, refresh } = useApi<CampaignsResponse>('/api/campaigns?days=30', getToken);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  /* Reads Google Ads now, then re-reads the screen. Two steps, because
     refreshing the page without re-reading Google would only redraw the same
     figures and look like nothing happened. */
  const refreshNow = async (): Promise<void> => {
    setSyncing(true);
    setSyncError(null);
    try {
      const token = await getToken();
      const r = await fetch(`${apiBaseUrl}/api/sync/google-ads`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Sync returned ${r.status}`);
      refresh();
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : 'Refresh failed');
    } finally {
      setSyncing(false);
    }
  };

  if (error) return <EmptyState title="Could not load campaigns" tag="Error">{error}</EmptyState>;
  if (!data) return <EmptyState title="Loading…" tag="Please wait">Reading campaign structure from the Growth Engine database.</EmptyState>;

  const live = data.campaigns.filter((c) => c.status === 'ENABLED');
  const served = data.campaigns.filter((c) => c.hasServed);
  const totalMonthly = data.campaigns.reduce((s, c) => s + (c.monthlyBudgetGbp ?? 0), 0);

  return (
    <div className="stack">
      {served.length === 0 ? (
        <div className="panel" style={{ borderColor: 'var(--warning)' }}>
          <h2>Nothing has served yet</h2>
          <p>
            {count(data.campaigns.length)} campaigns exist and are configured, but none has served an
            impression. Every performance column therefore reads <strong>{NO_DATA}</strong> rather than
            zero, because we have not measured no spend, we have not measured at all. Structure, budgets and bid
            strategy below are real and were read back from Google Ads.
          </p>
        </div>
      ) : null}

      <Panel
        title="Campaign structure"
        subtitle="Read back from Google Ads, not from the build specification. This is what the account actually contains."
      >
        <div className="kpi-grid">
          <CountKpi label="Campaigns" value={data.campaigns.length} note={`${live.length} enabled`} />
          <CountKpi label="Ad groups" value={data.campaigns.reduce((s, c) => s + c.adGroups, 0)} />
          <CountKpi label="Keywords" value={data.campaigns.reduce((s, c) => s + c.keywords, 0)} />
          <CountKpi
            label="Committed monthly budget"
            value={totalMonthly > 0 ? totalMonthly : null}
            money
            note="Daily budget × 30.4, if enabled"
          />
        </div>
      </Panel>

      <Panel title="Campaigns" subtitle={`Performance window: ${data.window.from} to ${data.window.to}.`}>
        <table className="data">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Status</th>
              <th>Bidding</th>
              <th className="num">Ad groups</th>
              <th className="num">Keywords</th>
              <th className="num">Daily budget</th>
              <th className="num">Impressions</th>
              <th className="num">Clicks</th>
              <th className="num">Spend</th>
              <th className="num">CPC</th>
              <th className="num">Conversions</th>
            </tr>
          </thead>
          <tbody>
            {data.campaigns.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td><span className={statusPill(c.status)}>{c.status}</span></td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                  {(c.biddingStrategy ?? NO_DATA).replace(/_/g, ' ')}
                </td>
                <td className="num">{count(c.adGroups)}</td>
                <td className="num">{count(c.keywords)}</td>
                <td className="num" style={faintIfNull(c.dailyBudgetGbp)}>{gbp(c.dailyBudgetGbp)}</td>
                <td className="num" style={faintIfNull(c.impressions)}>{count(c.impressions)}</td>
                <td className="num" style={faintIfNull(c.clicks)}>{count(c.clicks)}</td>
                <td className="num" style={faintIfNull(c.costGbp)}>{gbp(c.costGbp)}</td>
                <td className="num" style={faintIfNull(c.costPerClickGbp)}>{gbp(c.costPerClickGbp)}</td>
                <td className="num" style={faintIfNull(c.conversions)}>{count(c.conversions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 16, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn-ghost" onClick={() => { void refreshNow(); }} disabled={syncing}>
            {syncing ? 'Reading Google Ads\u2026' : 'Refresh from Google Ads'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>
            {data.lastSync.finishedUtc
              ? <>Figures last read from Google Ads{' '}
                  <strong>{new Date(data.lastSync.finishedUtc).toLocaleString('en-GB')}</strong>
                  {data.lastSync.status === 'FAILED'
                    ? <> , and that read FAILED, so the figures are older still.</>
                    : <>.</>}</>
              : <>No completed Google Ads read has been recorded, so the figures below cannot be
                 dated.</>}
            {data.lastSync.running ? <> A read is in progress now.</> : null}
            {' '}{data.lastSync.scheduleNote}
          </span>
        </div>
        {syncError ? (
          <p style={{ marginTop: 8, fontSize: 12, color: 'var(--negative)' }}>
            Could not refresh: {syncError}. The figures above are unchanged and still from the last
            successful read.
          </p>
        ) : null}
        <p style={{ marginTop: 14, fontSize: 12, color: 'var(--text-faint)' }}>
          Page drawn {new Date(data.generatedUtc).toLocaleString('en-GB')}, which is not the same as
          when the figures arrived. A campaign that has never served reports {NO_DATA} across every
          measured column; budget and structure are configuration, not measurement, and are shown
          regardless.
        </p>
      </Panel>
    </div>
  );
}
